import { Kind } from "graphql";
function fieldSegment(name) {
  return {
    kind: "field",
    name
  };
}
function inlineFragmentSegment(typeName) {
  return {
    kind: "inlineFragment",
    typeName
  };
}
function segmentsEqual(a, b) {
  if (a.kind !== b.kind) {
    return false;
  }
  if (a.kind === "field" && b.kind === "field") {
    return a.name === b.name;
  }
  if (a.kind === "inlineFragment" && b.kind === "inlineFragment") {
    return a.typeName === b.typeName;
  }
  return false;
}
function selectionMatchesSegment(selection, segment) {
  var _a;
  switch (segment.kind) {
    case "inlineFragment":
      return selection.kind === Kind.INLINE_FRAGMENT && ((_a = selection.typeCondition) == null ? void 0 : _a.name.value) === segment.typeName;
    case "field":
      return selection.kind === Kind.FIELD && selection.name.value === segment.name;
  }
}
function findNodeAtPath(selectionSet, path) {
  let current = selectionSet;
  for (let i = 0; i < path.length; i++) {
    const segment = path[i];
    const node = current.selections.find((s) => selectionMatchesSegment(s, segment));
    if (!node) {
      return void 0;
    }
    if (i === path.length - 1) {
      return node;
    }
    if (!node.selectionSet) {
      return void 0;
    }
    current = node.selectionSet;
  }
  return void 0;
}
function mapNodeAtPath(selectionSet, path, fn, pruneEmptyParent = true) {
  const [segment, ...rest] = path;
  const nodeIndex = selectionSet.selections.findIndex((s) => selectionMatchesSegment(s, segment));
  if (nodeIndex === -1) {
    return selectionSet;
  }
  const node = selectionSet.selections[nodeIndex];
  if (rest.length === 0) {
    const replacement = fn(node);
    const newSelections2 = [...selectionSet.selections];
    if (replacement === null) {
      newSelections2.splice(nodeIndex, 1);
    } else {
      newSelections2[nodeIndex] = replacement;
    }
    return {
      ...selectionSet,
      selections: newSelections2
    };
  }
  if (!node.selectionSet) {
    return selectionSet;
  }
  const newChildSet = mapNodeAtPath(node.selectionSet, rest, fn, pruneEmptyParent);
  if (newChildSet === node.selectionSet) {
    return selectionSet;
  }
  if (pruneEmptyParent && newChildSet.selections.length === 0) {
    const newSelections2 = selectionSet.selections.filter((_, i) => i !== nodeIndex);
    return {
      ...selectionSet,
      selections: newSelections2
    };
  }
  const updated = {
    ...node,
    selectionSet: newChildSet
  };
  const newSelections = [...selectionSet.selections];
  newSelections[nodeIndex] = updated;
  return {
    ...selectionSet,
    selections: newSelections
  };
}
function createSelectionForSegment(segment, selectionSet) {
  switch (segment.kind) {
    case "inlineFragment":
      return {
        kind: Kind.INLINE_FRAGMENT,
        typeCondition: {
          kind: Kind.NAMED_TYPE,
          name: {
            kind: Kind.NAME,
            value: segment.typeName
          }
        },
        directives: [],
        selectionSet: selectionSet ?? {
          kind: Kind.SELECTION_SET,
          selections: []
        }
      };
    case "field":
      return {
        kind: Kind.FIELD,
        name: {
          kind: Kind.NAME,
          value: segment.name
        },
        arguments: [],
        directives: [],
        selectionSet
      };
  }
}
function addField(selectionSet, path) {
  const [segment, ...rest] = path;
  if (rest.length === 0) {
    const alreadyPresent = selectionSet.selections.some((s) => selectionMatchesSegment(s, segment));
    if (alreadyPresent) {
      return selectionSet;
    }
    const newNode = createSelectionForSegment(segment);
    return {
      ...selectionSet,
      selections: [...selectionSet.selections, newNode]
    };
  }
  const existingIndex = selectionSet.selections.findIndex((s) => selectionMatchesSegment(s, segment));
  if (existingIndex === -1) {
    const emptySet = {
      kind: Kind.SELECTION_SET,
      selections: []
    };
    const parentNode = createSelectionForSegment(segment, addField(emptySet, rest));
    return {
      ...selectionSet,
      selections: [...selectionSet.selections, parentNode]
    };
  }
  const existing = selectionSet.selections[existingIndex];
  const childSet = existing.selectionSet ?? {
    kind: Kind.SELECTION_SET,
    selections: []
  };
  const updated = {
    ...existing,
    selectionSet: addField(childSet, rest)
  };
  const newSelections = [...selectionSet.selections];
  newSelections[existingIndex] = updated;
  return {
    ...selectionSet,
    selections: newSelections
  };
}
function removeField(selectionSet, path) {
  return mapNodeAtPath(selectionSet, path, () => null, true);
}
function replaceFieldSelectionSet(selectionSet, path, newSet) {
  return mapNodeAtPath(selectionSet, path, (node) => ({
    ...node,
    selectionSet: newSet
  }), false);
}
function setArgInSelectionSet(selectionSet, path, argName, value) {
  return mapNodeAtPath(
    selectionSet,
    path,
    (node) => {
      const field = node;
      const existingArgs = field.arguments ?? [];
      let newArgs;
      if (value === void 0) {
        newArgs = existingArgs.filter((a) => a.name.value !== argName);
      } else {
        const existingIndex = existingArgs.findIndex((a) => a.name.value === argName);
        const newArg = {
          kind: Kind.ARGUMENT,
          name: {
            kind: Kind.NAME,
            value: argName
          },
          value
        };
        if (existingIndex === -1) {
          newArgs = [...existingArgs, newArg];
        } else {
          newArgs = [...existingArgs];
          newArgs[existingIndex] = newArg;
        }
      }
      return {
        ...field,
        arguments: newArgs
      };
    },
    false
    // do not prune empty parents when setting args
  );
}
function findSelectionSet(selectionSet, path) {
  const node = findNodeAtPath(selectionSet, path);
  return node == null ? void 0 : node.selectionSet;
}
function findOperationIndex(doc, operationName) {
  if (operationName) {
    const named = doc.definitions.findIndex((d) => {
      var _a;
      return d.kind === Kind.OPERATION_DEFINITION && ((_a = d.name) == null ? void 0 : _a.value) === operationName;
    });
    if (named !== -1) {
      return named;
    }
  }
  return doc.definitions.findIndex((d) => d.kind === Kind.OPERATION_DEFINITION);
}
function findOperation(doc, operationName) {
  const index = findOperationIndex(doc, operationName);
  if (index === -1) {
    return void 0;
  }
  const operation = doc.definitions[index];
  return (operation == null ? void 0 : operation.kind) === Kind.OPERATION_DEFINITION ? operation : void 0;
}
function mapOperation(doc, operationName, fn) {
  const index = findOperationIndex(doc, operationName);
  if (index === -1) {
    return doc;
  }
  const def = doc.definitions[index];
  if ((def == null ? void 0 : def.kind) !== Kind.OPERATION_DEFINITION) {
    return doc;
  }
  const result = fn(def);
  if (result === def) {
    return doc;
  }
  const newDefinitions = [...doc.definitions];
  if (result === null) {
    newDefinitions.splice(index, 1);
  } else {
    newDefinitions[index] = result;
  }
  return {
    ...doc,
    definitions: newDefinitions
  };
}
function findDefinitionIndex(doc, target) {
  if (target.kind === "operation") {
    return findOperationIndex(doc, target.name);
  }
  return doc.definitions.findIndex((d) => d.kind === Kind.FRAGMENT_DEFINITION && d.name.value === target.name);
}
function findDefinition(doc, target) {
  const index = findDefinitionIndex(doc, target);
  if (index === -1) {
    return void 0;
  }
  const def = doc.definitions[index];
  if ((def == null ? void 0 : def.kind) === Kind.OPERATION_DEFINITION || (def == null ? void 0 : def.kind) === Kind.FRAGMENT_DEFINITION) {
    return def;
  }
  return void 0;
}
function mapDefinition(doc, target, fn) {
  const index = findDefinitionIndex(doc, target);
  if (index === -1) {
    return doc;
  }
  const def = doc.definitions[index];
  if ((def == null ? void 0 : def.kind) !== Kind.OPERATION_DEFINITION && (def == null ? void 0 : def.kind) !== Kind.FRAGMENT_DEFINITION) {
    return doc;
  }
  const result = fn(def);
  if (result === def) {
    return doc;
  }
  const newDefinitions = [...doc.definitions];
  if (result === null) {
    newDefinitions.splice(index, 1);
  } else {
    newDefinitions[index] = result;
  }
  return {
    ...doc,
    definitions: newDefinitions
  };
}
function definitionSelectionOffset(doc, target) {
  var _a;
  const loc = (_a = findDefinition(doc, target)) == null ? void 0 : _a.selectionSet.loc;
  return loc ? loc.start + 1 : void 0;
}
export {
  addField,
  createSelectionForSegment,
  definitionSelectionOffset,
  fieldSegment,
  findDefinition,
  findDefinitionIndex,
  findNodeAtPath,
  findOperation,
  findOperationIndex,
  findSelectionSet,
  inlineFragmentSegment,
  mapDefinition,
  mapNodeAtPath,
  mapOperation,
  removeField,
  replaceFieldSelectionSet,
  segmentsEqual,
  selectionMatchesSegment,
  setArgInSelectionSet
};
//# sourceMappingURL=ast-path.js.map
